<template>
  <div
    class="d-post-form"
    :class="{
      'mobile-open': isMobileOpen,
      'top-cta-fixed': isTopCtaFixed,
    }"
  >
    <div
      v-if="!activity"
      ref="topCta"
      class="mobile-top-cta"
    >
      <a
        title="Write an update"
        @click="openMobile"
      >
        <span class="title">Write an update</span>
        <DuiIconPlus class="icon-circle background-yellow-base icon-white" />
      </a>
    </div>

    <div class="post-form-container">
      <div class="post-form-compose">
        <div class="compose-box grid-flex grid-no-response">
          <div class="grid-item-shrink">
            <img
              class="avatar"
              :src="userAvatarUrl"
            >
          </div>
          <div class="grid-item">
            <DTextCompose
              ref="contentText"
              v-model="postInfo.content"
              :alt-font="true"
              :disabled="isProcessing"
              :mentions="postInfo.mentions"
              :placeholder="placeholderText"
            />
          </div>
          <div class="grid-item-shrink visible-until-sm">
            <button
              class="button-grey button-icon button-sm button-close"
              :disabled="isProcessing"
              @click="closeMobile"
            />
          </div>
        </div>

        <div
          v-if="isRepost"
          class="original-activity"
        >
          <DActivityActivity
            :activity="activity.source.repost"
            :is-repost="true"
          />
        </div>

        <div
          v-else
          class="post-types"
        >
          <button
            class="button-follow"
            :class="{ 'button-sand': postType === postTypes.recommendation }"
            :disabled="!!activity || isProcessing"
            @click="selectPostType(postTypes.recommendation)"
          >
            <DuiIconHeart />
            <span class="valign-middle">
              <span class="visible-until-sm">Recommend</span>
              <span class="hidden-until-md">Recommendation</span>

            </span>
          </button>

          <button
            class="button-follow"
            :class="{ 'button-sand': postType === postTypes.currentlyReading }"
            :disabled="!!activity || isProcessing"
            @click="selectPostType(postTypes.currentlyReading)"
          >
            <DuiIconGlasses
              class="icon-lg"
              style="margin: -4px 0;"
            />
            <span class="valign-middle">
              <span class="visible-until-sm">Reading</span>
              <span class="hidden-until-md">Currently reading</span>
            </span>
          </button>

          <button
            class="button-follow"
            :class="{ 'button-sand': postType === postTypes.image }"
            :disabled="!!activity || isProcessing"
            @click="selectPostType(postTypes.image)"
          >
            <DuiIconImage />
            <span class="valign-middle">Image</span>
          </button>

          <button
            class="button-follow"
            :class="{ 'button-sand': postType === postTypes.poll }"
            :disabled="!!activity || isProcessing"
            @click="selectPostType(postTypes.poll)"
          >
            <DuiIconPoll />
            <span class="valign-middle">Poll</span>
          </button>
        </div>

        <DPostBook
          v-if="(postType === postTypes.currentlyReading || postType === postTypes.recommendation)"
          ref="postBook"
          class="post-details split"
          :is-disabled="isProcessing"
          :post-info="postInfo"
          :with-rating="postType === postTypes.recommendation"
          @clear-post-type="clearPostType"
        />

        <DPostImage
          v-if="postType === postTypes.image"
          ref="postImage"
          class="post-details bordered"
          :is-disabled="isProcessing"
          :post-info="postInfo"
          @clear-post-type="clearPostType"
        />

        <DPostPoll
          v-if="postType === postTypes.poll"
          ref="postPoll"
          class="post-details bordered"
          :editing-poll-type="editingPollType"
          :is-disabled="isProcessing"
          :post-info="postInfo"
          @clear-post-type="clearPostType"
        />

        <div
          class="post-form-bottom-actions space-top-2em"
          :class="{
            'responsive': !!activity,
            'with-book': isAuthorBooksDisplayed,
          }"
        >
          <div class="author-books-container">
            <DuiDropdown
              v-if="isAuthorBooksDisplayed"
              ref="bookDropdown"
              v-model="postInfo.bookId"
              class="author-book sm"
              :disabled="isProcessing"
              label="Book"
              name="book"
              :nested="true"
              :options="authorBooks"
              placeholder="Attach this update to a book..."
              :search="true"
            />

            <button
              v-if="postInfo.bookId"
              class="button-sm button-icon button-close"
              @click.stop.prevent="clearAuthorBook"
            />
          </div>

          <div
            class="text-right"
            :class="{ 'button-pair right': activity }"
          >
            <button
              v-if="activity"
              :disabled="isProcessing"
              @click="disableEditPost"
            >
              Cancel
            </button>

            <button
              class="button-yellow"
              :class="{ 'space-right-none': !activity }"
              :disabled="isProcessing"
              @click="submitPost"
            >
              <span v-if="isProcessing">
                <span v-if="activity">Saving...</span>
                <span v-else>Sharing...</span>
                <DuiLoadingIndicator
                  class="space-left-1em"
                  size="sm"
                  style="margin: -4px 0;"
                />
              </span>

              <span v-else>
                <span v-if="activity">Save</span>
                <span v-else>Share</span>
              </span>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';

import DActivityActivity from '@d-components/d-activity-feed/d-activity-entry/d-activity-activity.vue';
import DTextCompose from '@d-components/d-text-compose/d-text-compose.vue';
import DropdownOptionsHelper from '@d-helpers/dropdown-options-helper';
import activityFeedService from '@d-services/activity-feed-service/activity-feed-service';
import authorBooksService from '@d-services/author-books-service/author-books-service';
import { POST_TYPES, SCROLL_OPTIONS } from '@d-shared/config';
import ElementPosition from '@d-shared/utils/element-position';
import { getErrorMessage } from '@d-shared/utils/errors';
import SASS from '@dui/helpers/sass';

import DPostBook from './d-post-book/d-post-book.vue';
import DPostImage from './d-post-image/d-post-image.vue';
import DPostPoll, { POLL_TYPES } from './d-post-poll/d-post-poll.vue';

const EMPTY_POST_INFO = Object.freeze({
  bookId: null,
  bookFromUrl: null,
  bookUrl: null,
  bookUrlInvalid: false,
  compareImages: null,
  content: null,
  image: null,
  isEditing: false,
  mentions: [],
  multipleChoiceOptions: null,
  recommendationRating: null,
});

export default {
  components: {
    DActivityActivity,
    DPostBook,
    DPostImage,
    DPostPoll,
    DTextCompose,
  },
  props: {
    activity: {
      type: Object,
      default: null,
    },
    isAuthor: {
      type: Boolean,
      default: false,
    },
    userAvatarUrl: {
      type: String,
      required: true,
    },
  },
  data: () => ({
    authorBooks: null,
    editingPollType: null,
    isMobileOpen: false,
    isProcessing: false,
    isRepost: false,
    isTopCtaFixed: false,
    postInfo: Object.assign({}, EMPTY_POST_INFO),
    postType: POST_TYPES.default,
    postTypes: POST_TYPES,
    refBody: document.body,
    resizeObserver: null,
    topCtaInitialPosition: null,
    validationMessages: [],
  }),
  computed: {
    isAuthorBooksDisplayed() {
      if (this.isRepost) {
        return false;
      }

      if (!this.isAuthor) {
        return false;
      }

      if (this.postType === POST_TYPES.currentlyReading ||
        this.postType === POST_TYPES.recommendation) {
        return false;
      }

      return true;
    },

    isPostEnabled() {
      if (this.postType === POST_TYPES.recommendation) {
        return ((!!this.postInfo.bookFromUrl || !!this.postInfo.bookUrl)
          && !!this.postInfo.recommendationRating);
      }

      if (this.postType === POST_TYPES.currentlyReading) {
        return (!!this.postInfo.bookFromUrl || !!this.postInfo.bookUrl);
      }

      if (this.postType === POST_TYPES.image) {
        return (!!this.postInfo.image && !!this.postInfo.image.src);
      }

      if (this.postType === POST_TYPES.poll) {
        return !!this.postInfo.content &&
          (!!this.postInfo.compareImages || !!this.postInfo.multipleChoiceOptions);
      }

      if (this.isRepost) {
        return true;
      }

      return !!this.postInfo.content;
    },

    placeholderText() {
      switch (this.postType) {
      case POST_TYPES.currentlyReading:
        return 'Share what you are reading...';
      case POST_TYPES.poll:
        return 'Add a question...';
      case POST_TYPES.recommendation:
        return 'Recommend a book...';
      default:
        return 'Write an update...';
      }
    },
  },
  watch: {
    postInfo: {
      deep: true,
      handler: function() {
        this.checkPostComposingStatusAndGuard();
      },
    },
  },
  beforeDestroy() {
    this.removeResizeObserver();
    this.removeEventListeners();
    Vue.duiPageGuardManager.unguard();
  },
  mounted() {
    this.init();
  },
  methods: {
    attachAuthorBookInfo(postData) {
      if (!this.postInfo.bookId) {
        return;
      }

      postData.post.book_id = this.postInfo.bookId;
    },

    // Book info
    attachBookFromUrlInfo(postData) {
      if (!this.postInfo.bookFromUrl) {
        postData.post.external_book_url = this.postInfo.bookUrl;
        return;
      }

      if (!!this.postInfo.bookFromUrl.provider) {
        postData.post.external_book_id = this.postInfo.bookFromUrl.slug;
        return;
      }

      postData.post.book_id = this.postInfo.bookFromUrl.slug;
    },
    // END  Book info

    checkPostComposingStatusAndGuard() {
      if (this.isPostBeingComposed()) {
        Vue.duiPageGuardManager.guard();
        return;
      }

      Vue.duiPageGuardManager.unguard();
    },

    clearAuthorBook() {
      this.$set(this.postInfo, 'bookId', null);

      if (this.$refs.bookDropdown) {
        this.$refs.bookDropdown.deselectAll();
      }
    },

    clearPostInfo() {
      this.$refs.contentText.clear();
      this.$set(this, 'postInfo', Object.assign({}, EMPTY_POST_INFO));
    },

    clearPostType() {
      if (!!this.activity) {
        return;
      }

      this.$set(this, 'postType', POST_TYPES.default);
      this.clearAuthorBook();
    },

    closeMobile() {
      if (!!this.activity) {
        this.disableEditPost();
        return;
      }

      this.$set(this, 'isMobileOpen', false);
    },

    closeMobileAndScroll() {
      this.closeMobile();

      if (window.innerWidth > SASS.screenSize.md) {
        return;
      }

      const scrollOptions = Object.assign({}, SCROLL_OPTIONS);
      scrollOptions.offset = -72;
      this.$scrollTo(this.$el, scrollOptions);
    },

    disableEditPost() {
      this.$set(this.activity, 'isEditing', false);
    },

    async getAuthorBooks() {
      if (!this.isAuthor) {
        return;
      }

      try {
        let authorBooks = await authorBooksService.getBooks();
        authorBooks = DropdownOptionsHelper.getBooksListAsOptions(authorBooks, true);
        this.$set(this, 'authorBooks', authorBooks);
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }
    },

    getPostInfo() {
      const postData = {
        post: {},
      };

      if (this.postInfo.content) {
        postData.post.content = this.postInfo.content;
      }

      const mentions = this.$refs.contentText.getMentions();
      if (mentions && mentions.length) {
        postData.post.mentions_list = mentions;
      }

      switch (this.postType) {
      case POST_TYPES.currentlyReading:
        postData.post.type = 'CurrentlyReadingPost';
        this.attachBookFromUrlInfo(postData);
        break;
      case POST_TYPES.recommendation:
        postData.post.type = 'RecommendationPost';
        this.attachBookFromUrlInfo(postData);
        postData.post.rating = this.postInfo.recommendationRating;
        break;
      case POST_TYPES.image:
        postData.post.type = 'ImagePost';
        this.attachAuthorBookInfo(postData);
        postData.post.image = this.postInfo.image.file;
        break;
      case POST_TYPES.poll:
        this.attachAuthorBookInfo(postData);

        if (!!this.postInfo.compareImages) {
          postData.post.type = 'CompareImagesPost';
          postData.post.option_1 = this.postInfo.compareImages.image1.file;
          postData.post.option_1_label = this.postInfo.compareImages.image1.label;
          postData.post.option_2 = this.postInfo.compareImages.image2.file;
          postData.post.option_2_label = this.postInfo.compareImages.image2.label;
          break;
        }

        postData.post.type = 'MultipleChoicePost';
        postData.post.options = this.postInfo.multipleChoiceOptions;
        break;
      default:
        postData.post.type = 'Post';

        if (this.isRepost) {
          postData.post.repost_id = this.activity.source.repost.slug;
          break;
        }

        this.attachAuthorBookInfo(postData);
        break;
      }

      return postData;
    },

    init() {
      this.getAuthorBooks();
      this.setEditingState();

      if (this.activity) {
        return;
      }

      this.setTopCtaInitialPosition();
      this.addEventListeners();
      this.addResizeObserver();
    },

    addResizeObserver() {
      this.resizeObserver = new ResizeObserver(this.setTopCtaInitialPosition);
      this.resizeObserver.observe(this.refBody);
    },

    removeResizeObserver() {
      if (!this.resizeObserver) {
        return;
      }

      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    },

    isPostBeingComposed() {
      return this.postInfo.bookId ||
        this.postInfo.bookFromUrl ||
        this.postInfo.bookUrl ||
        this.postInfo.compareImages ||
        this.postInfo.content ||
        this.postInfo.image ||
        this.postInfo.multipleChoiceOptions ||
        this.postInfo.recommendationRating;
    },

    openMobile() {
      this.$set(this, 'isMobileOpen', true);
    },

    selectPostType(postType) {
      if (!!this.activity) {
        return;
      }

      this.clearAuthorBook();
      this.clearFormValidations();

      if (this.postType === postType) {
        this.$set(this, 'postType', POST_TYPES.default);
        return;
      }

      this.$set(this, 'postType', postType);
    },

    setTopCtaInitialPosition() {
      const initialCtaTop = ElementPosition.getElementCurrentTopPosition(this.$refs.topCta);
      this.$set(this, 'topCtaInitialPosition', initialCtaTop);
    },

    async submitPost() {
      if (this.isProcessing) {
        return;
      }

      this.updateFormValidations();

      if (!this.isPostEnabled) {
        return;
      }

      this.$set(this, 'isProcessing', true);

      try {
        const postData = this.getPostInfo();

        if (!!this.activity) {
          const editedPost = await activityFeedService.updatePost(this.activity.slug, postData);
          this.$emit('post-edited', editedPost);
          Vue.duiNotificationManager.success('Post saved!');
          this.$set(this, 'isProcessing', false);
          this.disableEditPost();
          Vue.duiPageGuardManager.unguard();
          return;
        }

        const newPost = await activityFeedService.addPost(postData);
        this.$emit('post-created', newPost);
        Vue.duiNotificationManager.success('Post published!');
        this.clearPostInfo();
        this.clearPostType();
        this.closeMobileAndScroll();
      } catch (error) {
        Vue.duiNotificationManager.error(getErrorMessage(error));
      }

      this.$set(this, 'isProcessing', false);
    },

    // Editing state
    setEditingAuthorBook() {
      if (!this.activity.source.book ||
        this.activity.source.type === 'CurrentlyReadingPost' ||
        this.activity.source.type === 'RecommendationPost'
      ) {
        return;
      }

      this.$set(this.postInfo, 'bookId', this.activity.source.book.slug);
    },

    setEditingBookInfo() {
      if (this.activity.source.external_book) {
        this.$set(this.postInfo, 'bookFromUrl', this.activity.source.external_book);
        return;
      }

      if (this.activity.source.book) {
        this.$set(this.postInfo, 'bookFromUrl', this.activity.source.book);
        return;
      }

      this.$set(this.postInfo, 'bookUrl', this.activity.source.external_book_url);
    },

    setEditingContent() {
      switch (this.activity.source.type) {
      case 'CurrentlyReadingPost':
        this.$set(this, 'postType', POST_TYPES.currentlyReading);
        this.setEditingBookInfo();
        break;
      case 'RecommendationPost':
        this.$set(this, 'postType', POST_TYPES.recommendation);
        this.$set(this.postInfo, 'recommendationRating', this.activity.source.verdict.rating);
        this.setEditingBookInfo();
        break;
      case 'ImagePost':
        this.$set(this, 'postType', POST_TYPES.image);
        this.$set(this.postInfo, 'image', { src: this.activity.source.image.medium.url });
        break;
      case 'CompareImagesPost':
      case 'MultipleChoicePost':
        this.$set(this, 'postType', POST_TYPES.poll);
        this.setEditingPoll();
        break;
      default:
        this.$set(this, 'postType', POST_TYPES.default);
        break;
      }
    },

    setEditingPoll() {
      if (this.activity.source.type === 'MultipleChoicePost') {
        this.$set(this, 'editingPollType', POLL_TYPES.multipleChoice);
        const options = Object.keys(this.activity.source.options_with_votes);
        this.$set(this.postInfo, 'multipleChoiceOptions', options);
        return;
      }

      this.$set(this, 'editingPollType', POLL_TYPES.compareImages);
      this.$set(this.postInfo, 'compareImages', {
        image1: {
          file: null,
          label: this.activity.source.option_1_label,
          src: this.activity.source.option_1.medium.url,
        },
        image2: {
          file: null,
          label: this.activity.source.option_2_label,
          src: this.activity.source.option_2.medium.url,
        },
      });
    },

    async setEditingState() {
      if (!this.activity) {
        return;
      }

      const isRepost = !!(this.activity && this.activity.source && this.activity.source.repost);
      this.$set(this, 'isRepost', isRepost);
      this.$set(this.postInfo, 'content', this.activity.source.content);
      this.$set(this.postInfo, 'mentions', this.activity.source.mentions);
      this.$set(this.postInfo, 'isEditing', true);
      this.setEditingAuthorBook();
      this.setEditingContent();

      await this.$nextTick();
      this.$refs.contentText.setEditorContentFromText(this.postInfo.content);
      this.$refs.contentText.focus();
      this.$set(this, 'isMobileOpen', true);
    },
    // END Editing state

    // Form validation
    clearFormValidations() {
      this.$set(this, 'validationMessages', []);
    },

    updateFormValidations() {
      const validationMessages = this.validateFormAndGetMessages();
      this.$set(this, 'validationMessages', validationMessages);

      this.validationMessages.forEach((message) => {
        Vue.duiNotificationManager.message(message);
      });
    },

    validateFormAndGetMessages() {
      const FORM_VALIDATION_MESSAGES = {
        bookUrl: 'Book URL is empty',
        bookUrlInvalid: 'Book URL is invalid',
        image: 'You must add an image',
        pollOptions: 'Poll options are not set',
        postContent: 'Post content is empty',
        question: 'You must ask a question',
        rating: 'You must provide a rating',
      };

      const validationMessages = [];

      if (this.postType === POST_TYPES.currentlyReading) {
        if (this.postInfo.bookUrlInvalid) {
          validationMessages.push(FORM_VALIDATION_MESSAGES.bookUrlInvalid);
          return validationMessages;
        }

        if (!this.postInfo.bookFromUrl && !this.postInfo.bookUrl) {
          validationMessages.push(FORM_VALIDATION_MESSAGES.bookUrl);
          this.$refs.postBook.blinkUrl();
        }

        return validationMessages;
      }

      if (this.postType === POST_TYPES.recommendation) {
        if (this.postInfo.bookUrlInvalid) {
          validationMessages.push(FORM_VALIDATION_MESSAGES.bookUrlInvalid);
          return validationMessages;
        }

        if (!this.postInfo.bookFromUrl && !this.postInfo.bookUrl) {
          validationMessages.push(FORM_VALIDATION_MESSAGES.bookUrl);
          this.$refs.postBook.blinkUrl();
        }

        if (!this.postInfo.recommendationRating) {
          validationMessages.push(FORM_VALIDATION_MESSAGES.rating);
          this.$refs.postBook.blinkRating();
        }

        return validationMessages;
      }

      if (this.postType === POST_TYPES.image) {
        if (!this.postInfo.image || !this.postInfo.image.src) {
          validationMessages.push(FORM_VALIDATION_MESSAGES.image);
          this.$refs.postImage.blinkImage();
        }

        return validationMessages;
      }

      if (this.postType === POST_TYPES.poll) {
        if (!this.postInfo.content) {
          validationMessages.push(FORM_VALIDATION_MESSAGES.question);
          this.$refs.contentText.focus();
        }

        if (!this.postInfo.compareImages && !this.postInfo.multipleChoiceOptions) {
          validationMessages.push(FORM_VALIDATION_MESSAGES.pollOptions);
          this.$refs.postPoll.blinkPoll();
        }

        return validationMessages;
      }

      if (!this.postInfo.content && !this.isRepost) {
        validationMessages.push(FORM_VALIDATION_MESSAGES.postContent);
        this.$refs.contentText.focus();
      }

      return validationMessages;
    },
    // END Form validation

    // Events
    addEventListeners() {
      window.addEventListener('resize', this.windowChangePerformed);
      window.addEventListener('scroll', this.windowChangePerformed);
    },

    removeEventListeners() {
      window.removeEventListener('resize', this.windowChangePerformed);
      window.removeEventListener('scroll', this.windowChangePerformed);
    },

    windowChangePerformed() {
      this.$set(this, 'isTopCtaFixed', (window.scrollY > this.topCtaInitialPosition));
    },
    // END Events
  },
};
</script>

<style lang="scss">
@import "@dui-styles/base";
@import "../activity";

$z-index-mobile-overlay: 3;
$z-index-mobile-top-cta: 2;
$z-index-post-form-container: ($z-index-modal - 1);

.d-post-form {
  margin: 0 (-$_2em);

  @include screenMoreThan(sm) {
    margin: 0;
  }

  .mobile-top-cta {
    background-color: getColor($black);
    margin-top: -$_1em;
    padding: $_1em;

    @include transition((
      box-shadow
    ));

    .with-white-cta & {
      background-color: getColor($white);

      & a {
        border: $border-width-base solid getColor($yellow);
        color: getColor($black);
      }
    }

    @include screenMoreThan(sm) {
      display: none;
    }

    a {
      align-items: center;
      background-color: $color-translucent-white;
      border-radius: $border-radius-sm;
      color: getColor($white);
      display: flex;
      padding: $_1em $_1-5em;

      .title {
        flex: 1;
      }
    }
  }

  .post-form-compose {
    background-color: getColor($white);
    border: $border-width-base solid getColor($yellow);
    border-radius: $border-radius-lg;
    box-shadow: $box-shadow-base;
    padding: $_2em;
  }

  .post-form-header {
    padding-bottom: $_4em;

    .content {
      width: 75%;
      @include screenMoreThan(sm) {
        margin-left: $_2em;
      }
    }
  }

  .post-form-bottom-actions {
    .dui-dropdown.author-book {
      min-width: 0;
    }

    &.with-book {
      align-items: end;
      display: grid;
      grid-template-columns: 1fr auto;
      grid-column-gap: $_2em;

      .author-books-container {
        min-width: 0;
        position: relative;

        .button-close {
          position: absolute;
          right: $_2em;
          top: 50%;
          transform: translateY(-50%);
        }
      }
    }
  }

  .compose-box {
    .avatar {
      border-radius: 50%;
      height: $_4em;
      margin-right: $_1em;
      width: $_4em;
    }

    .d-text-compose {
      height: 100%;
      padding: $_0-5em 0 0 $_0-5em;

      @include activityDTextCompose();
    }
  }

  .post-types {
    display: grid;
    grid-column-gap: $_1em;
    grid-row-gap: $_1em;
    grid-template-columns: repeat(2, 1fr);
    margin-top: $_2em;

    @include screenMoreThan(md) {
      display: block;
    }

    button {
      font-size: $font-size-sm;

      .dui-icon {
        margin-right: $_0-5em;
      }
    }
  }

  .post-details {
    margin-top: $_2em;

    &.bordered {
      border: $border-width-base solid getColor($grey, medium);
      border-radius: $border-radius-base;
      padding: $_2em;
    }

    &.split {
      border-bottom: $border-width-base solid getColor($grey, medium);
      border-top: $border-width-base solid getColor($grey, medium);
      padding: $_2em 0;
    }

    h4 {
      font-size: $font-size-base;
      margin: 0;
    }
  }

  .original-activity {
    @include originalActivityStyling();

    .d-activity-activity {
      @include activityStyling();
    }
  }

  @include screenLessThan(md) {
    &:before {
      background-color: $color-translucent-black-dark;
      content: " ";
      height: 100%;
      left: 0;
      opacity: 0;
      pointer-events: none;
      position: fixed;
      top: 0;
      transform: translate3d(0,0,0);
      width: 100%;
      z-index: $z-index-mobile-overlay;

      @include transition((
        opacity
      ));
    }

    .post-form-container {
      bottom: -100vh;
      height: 100vh;
      left: 0;
      overflow-y: scroll;
      -webkit-overflow-scrolling: touch;
      pointer-events: none;
      position: fixed;
      transform: translate3d(0,0,0);
      width: 100%;
      z-index: $z-index-post-form-container;

      @include transition((
        bottom
      ));

      .post-form-compose {
        border: none;
        border-bottom-left-radius: 0;
        border-bottom-right-radius: 0;
        box-sizing: border-box;
        display: grid;
        width: 100%;
        grid-template-rows: 1fr auto auto;
        margin-top: 25vh;
        min-height: 75vh;

        .dui-dropdown.author-book {
          @include duiDropdownUpwards();
        }
      }

      .post-form-bottom-actions.responsive {
        grid-row-gap: $_2em;

        > * {
          grid-column: span 2;
        }
      }
    }

    &.mobile-open {
      &:before {
        opacity: 1;
      }

      .post-form-container {
        bottom: 0;
        pointer-events: all;
      }
    }

    &.top-cta-fixed {
      margin-bottom: $_7em;

      .mobile-top-cta {
        box-sizing: border-box;
        left: 0;
        position: fixed;
        margin: 0;
        top: 0;
        transform: translate3d(0,0,0);
        width: 100%;
        z-index: $z-index-mobile-top-cta;

        a {
          width: calc(100% - #{ $_3em });
        }

        .with-white-cta & {
          box-shadow: $box-shadow-base;

          & a {
            width: calc(100% - #{ $_3em + (2 * $border-width-base) });
          }
        }
      }
    }

    .compose-box textarea {
      min-height: 100%;
    }
  }
}
</style>
